import type { TooltipContentProps } from "recharts";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { ClientOnly } from "~/components/ui/client-only";
import { formatCurrency } from "~/lib/format";
import type { YearSummary } from "~/types/financials";

interface AssetCompositionChartProps {
	year: YearSummary;
}

interface CompositionDatum {
	name: string;
	value: number;
}

const ASSET_COLORS = ["var(--color-chart-1)", "var(--color-chart-2)"];
const LIABILITY_COLORS = ["var(--color-chart-2)", "var(--color-chart-3)"];

function CompositionTooltip({ active, payload }: TooltipContentProps) {
	if (!active || !payload?.length) return null;
	const datum = payload[0]?.payload as CompositionDatum | undefined;
	if (!datum) return null;

	return (
		<div className="glass-panel-strong rounded-xl px-3 py-2 text-xs">
			<p className="font-medium text-text">{datum.name}</p>
			<p className="mt-0.5 font-mono font-semibold tabular-nums text-primary-700">
				{formatCurrency(datum.value)}
			</p>
		</div>
	);
}

function CompositionDonut({
	title,
	data,
	colors,
}: {
	title: string;
	data: CompositionDatum[];
	colors: string[];
}) {
	const total = data.reduce((sum, item) => sum + item.value, 0);

	return (
		<div className="glass-control rounded-2xl p-4">
			<div className="flex items-center justify-between gap-3">
				<h4 className="text-sm font-semibold">{title}</h4>
				<span className="font-mono text-xs font-medium tabular-nums text-muted">
					{formatCurrency(total)}
				</span>
			</div>
			<div className="mt-3 grid items-center gap-4 sm:grid-cols-[180px_1fr]">
				<div
					className="relative mx-auto h-44 w-44"
					role="img"
					aria-label={`${title}: ${formatCurrency(total)}`}
				>
					<ResponsiveContainer width="100%" height="100%">
						<PieChart>
							<Pie
								data={data}
								dataKey="value"
								nameKey="name"
								cx="50%"
								cy="50%"
								innerRadius="62%"
								outerRadius="86%"
								paddingAngle={4}
								cornerRadius={7}
								stroke="var(--color-surface)"
								strokeOpacity={0.72}
								strokeWidth={2}
								isAnimationActive={false}
							>
								{data.map((entry, index) => (
									<Cell key={entry.name} fill={colors[index % colors.length]} />
								))}
							</Pie>
							<Tooltip content={CompositionTooltip} />
						</PieChart>
					</ResponsiveContainer>
					<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
						<span className="label-overline">Celkem</span>
						<span className="mt-1 max-w-28 text-center font-mono text-sm font-semibold tabular-nums">
							{formatCurrency(total)}
						</span>
					</div>
				</div>
				<div className="flex flex-col gap-2.5">
					{data.map((item, index) => (
						<div key={item.name} className="flex items-center justify-between gap-3 text-xs">
							<span className="flex min-w-0 items-center gap-2 text-muted">
								<span
									className="h-2.5 w-2.5 shrink-0 rounded-full"
									style={{ backgroundColor: colors[index % colors.length] }}
								/>
								<span className="truncate">{item.name}</span>
							</span>
							<span className="shrink-0 font-mono font-medium tabular-nums">
								{Math.round((item.value / total) * 100)} %
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export function AssetCompositionChart({ year }: AssetCompositionChartProps) {
	const assetData = [
		{ name: "Stálá aktiva", value: year.fixedAssets },
		{ name: "Oběžná aktiva", value: year.currentAssets },
	].filter((item): item is CompositionDatum => item.value != null && item.value > 0);

	const liabilityData = [
		{ name: "Vlastní kapitál", value: year.equity },
		{ name: "Cizí zdroje", value: year.foreignResources },
	].filter((item): item is CompositionDatum => item.value != null && item.value > 0);

	// A pie chart cannot represent negative slices, so negative equity is
	// excluded from the "Pasiva" pie above. Surface it explicitly instead of
	// silently dropping it, otherwise the structure looks misleadingly wrong.
	const hasNegativeEquity = year.equity != null && year.equity < 0;

	if (assetData.length === 0 && liabilityData.length === 0 && !hasNegativeEquity) return null;

	return (
		<div className="card-surface rounded-2xl p-6">
			<div>
				<p className="label-overline">Rozvaha {year.year}</p>
				<h3 className="mt-1 text-lg font-semibold">Struktura majetku a zdrojů</h3>
			</div>
			<ClientOnly fallback={<div className="h-[280px] animate-pulse bg-muted rounded" />}>
				<div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
					{assetData.length > 0 && (
						<CompositionDonut title="Aktiva" data={assetData} colors={ASSET_COLORS} />
					)}
					{liabilityData.length > 0 && (
						<CompositionDonut title="Pasiva" data={liabilityData} colors={LIABILITY_COLORS} />
					)}
				</div>
				{hasNegativeEquity && (
					<p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
						Vlastní kapitál je záporný ({formatCurrency(year.equity)}) a není zobrazen v grafu
						pasiv. Záporný vlastní kapitál znamená, že závazky převyšují majetek společnosti.
					</p>
				)}
			</ClientOnly>
		</div>
	);
}
